import {Button} from 'shells/button/button';

import type {GetLimits_limits as Limits} from 'nutshell-graphql-types';

import {launchIntercomMessenger} from '../../intercom/launch-intercom-messenger';
import {useGetAvailablePlans} from '../../setup/billing/graphql/hooks/use-get-available-plans';

import {GateContentLayout} from './gate-content-layout';
import {findPlanByName, getPerUserLimit} from './helpers';
import {ManagePlanLink} from './manage-plan-link';
import {OutOfCreditsAdminContent} from './out-of-credits-admin-content';
import {OutOfCreditsNonAdminContent} from './out-of-credits-non-admin-content';
import {OutOfCreditsTrialAdminContent} from './out-of-credits-trial-admin-content';
import {OutOfCreditsTrialNonAdminContent} from './out-of-credits-trial-non-admin-content';

interface Props {
    isAdmin: boolean;
    limits: Limits | null;
    onClose: () => void;
}

export function OutOfCreditsContent(props: Props) {
    const {crm, isLoading} = useGetAvailablePlans(NutClientConfig.isTrial);
    const isLoadingPlans = isLoading && !crm;

    const activePlan = crm?.plans?.find((plan) => plan.isActive);
    const numCrmSeats = activePlan?.quantity ?? 1;
    const currentPerUser = getPerUserLimit(props.limits?.currentPlanLimit ?? 0, numCrmSeats);
    const upgradePerUser = getPerUserLimit(props.limits?.upgradePlanLimit ?? 0, numCrmSeats);

    const upgradePlanName = props.limits?.upgradePlanName ?? '';
    const upgradePlan = crm?.plans ? findPlanByName(crm.plans, upgradePlanName) : undefined;

    const isHighestPlan = props.limits?.currentPlanName === props.limits?.upgradePlanName;

    if (NutClientConfig.isTrial) {
        if (props.isAdmin) {
            return (
                <GateContentLayout
                    onClose={props.onClose}
                    footerAction={
                        <ManagePlanLink
                            onClose={props.onClose}
                            variant='button-ai-purple-dark'
                            label='Choose a plan'
                        />
                    }
                    centerContent={true}
                >
                    <OutOfCreditsTrialAdminContent limits={props.limits} />
                </GateContentLayout>
            );
        }

        return (
            <GateContentLayout onClose={props.onClose} centerContent={true}>
                <OutOfCreditsTrialNonAdminContent limits={props.limits} />
            </GateContentLayout>
        );
    }

    if (props.isAdmin) {
        const footerAction = isHighestPlan ? (
            <Button size='big' variant='ai-purple-dark' onClick={launchIntercomMessenger}>
                Contact us
            </Button>
        ) : (
            <ManagePlanLink onClose={props.onClose} variant='button-ai-purple-dark' />
        );

        return (
            <GateContentLayout
                onClose={props.onClose}
                centerContent={isHighestPlan}
                footerAction={footerAction}
            >
                <OutOfCreditsAdminContent
                    limits={props.limits}
                    isLoadingPlans={isLoadingPlans}
                    currentPerUser={currentPerUser}
                    upgradePerUser={upgradePerUser}
                    upgradePlan={upgradePlan}
                    isHighestPlan={isHighestPlan}
                />
            </GateContentLayout>
        );
    }

    return (
        <GateContentLayout onClose={props.onClose} centerContent={true}>
            <OutOfCreditsNonAdminContent limits={props.limits} isHighestPlan={isHighestPlan} />
        </GateContentLayout>
    );
}
